import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Link from "@mui/material/Link";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { skipToken } from "@reduxjs/toolkit/query";
import { getSaleByOrderNumber } from "@/api/sale/getSaleByOrderNumber";
import type { DispatchReportRangeItem } from "@/api/report/_models/DispatchReport";
import { ReconciliationDispatchRowTemplate } from "./ReconciliationDispatchRowTemplate";
import { ReconciliationDispatchStatusChip } from "./ReconciliationDispatchStatusChip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

const HEADER_OFFSET = 33;

/** Deep link to the sale in the Cin7 Core web app, on its Order tab. */
const cin7SaleUrl = (saleId: string): string =>
  `https://inventory.dearsystems.com/Sale#${saleId}~tabOrder`;

interface ReconciliationDispatchGroupProps {
  orderReference: string;
  lines: DispatchReportRangeItem[];
  isCurrent: boolean;
  onSettled: () => void;
  /** Reports this order's resolved Cin7 shipping status (null if not found). */
  onResolved: (orderReference: string, shippingStatus: string | null) => void;
}
export const ReconciliationDispatchGroup = (props: ReconciliationDispatchGroupProps) => {
  const { orderReference, lines, isCurrent, onSettled, onResolved } = props;
  const [collapsed, setCollapsed] = useState(true);
  const [everActive, setEverActive] = useState(false);
  const hasReported = useRef(false);

  const orderNumber = orderReference.replace(/^SBM-/, "");
  const query = getSaleByOrderNumber.useQuery(everActive ? orderNumber : skipToken);
  const isLoading = query.isLoading;
  const sale = query.data ?? null;

  useEffect(() => {
    if (isCurrent) {
      setEverActive(true);
    }
  }, [isCurrent]);

  useEffect(() => {
    if (isCurrent && !hasReported.current && (query.isSuccess || query.isError)) {
      hasReported.current = true;
      onResolved(orderReference, query.data?.shippingStatus ?? null);
      onSettled();
    }
  }, [isCurrent, query.isSuccess, query.isError, query.data, orderReference, onResolved, onSettled]);

  const toggle = () => {
    setCollapsed((value) => !value);
  };

  const openInCin7 = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!sale) {
      return;
    }
    window.open(cin7SaleUrl(sale.id), "_blank", "noopener");
  };

  return (
    <>
      <Box
        onClick={() => toggle()}
        sx={{
          position: "sticky",
          top: HEADER_OFFSET,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 2,
          py: 0.75,
          cursor: "pointer",
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          fontWeight: "bold",
          height: 48
        }}
      >
        {collapsed ?
          <KeyboardArrowRightIcon fontSize="small" /> :
          <KeyboardArrowDownIcon fontSize="small" />
        }

        <Typography sx={{ fontSize: 14, fontWeight: "bold", mr: 1 }}>
          {orderReference}
        </Typography>

        <Box sx={{ flex: 1 }}>
          <ReconciliationDispatchStatusChip
            isLoading={isLoading}
            shippingStatus={sale?.shippingStatus ?? null}
          />
        </Box>

        <Tooltip title="Open in Cin7">
          <span>
            <IconButton
              onClick={(event) => openInCin7(event)}
              disabled={!sale}
            >
              <OpenInNewIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Collapse in={!collapsed}>
        {lines.map((row, i) => (
          <ReconciliationDispatchRowTemplate
            key={i}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box sx={{ gridArea: "consignment" }}>{row.consignmentNumber}</Box>
            <Box sx={{ gridArea: "item" }}>{row.item}</Box>
            <Box sx={{ gridArea: "date" }}>{row.date}</Box>
            <Box sx={{ gridArea: "qty", textAlign: "right" }}>{row.quantity}</Box>
            <Box sx={{ gridArea: "carrier" }}>{row.tracking.carrier}</Box>
            <Box sx={{ gridArea: "tracking" }}>
              {row.tracking.url && (
                <Link
                  href={row.tracking.url}
                  target="_blank"
                  rel="noopener"
                >
                  {row.tracking.number}
                </Link>
              )}
              {!row.tracking.url && row.tracking.number}
            </Box>
            <Box sx={{ gridArea: "source" }}>{row.sourceFile}</Box>
          </ReconciliationDispatchRowTemplate>
        ))}
      </Collapse>
    </>
  );
};
